import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Printer, Download, FileText, FileSpreadsheet, Filter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHANNEL_LABEL = { call: "Call", whatsapp: "WhatsApp", visit: "Visit", email: "Email", office_visit: "Office Visit" };
const STATUS_STYLES = { scheduled: "bg-amber-100 text-amber-800", done: "bg-emerald-100 text-emerald-800", missed: "bg-red-100 text-red-800" };

export default function Followups() {
  const [filters, setFilters] = useState({ status: "", followup_type: "", executive_id: "", customer_id: "", date_from: "", date_to: "", bucket: "" });
  const { data: counts } = useQuery({ queryKey: ["fu-counts"], queryFn: async () => (await api.get("/followups/counts")).data });
  const { data: employees = [] } = useQuery({ queryKey: ["fu-emp"], queryFn: async () => { try { return (await api.get("/employees")).data; } catch { return []; } } });
  const { data: customers = [] } = useQuery({ queryKey: ["fu-cust"], queryFn: async () => { try { return (await api.get("/customers", { params: { limit: 200 } })).data; } catch { return []; } } });

  const cleanFilters = useMemo(() => {
    const p = {};
    for (const [k, v] of Object.entries(filters)) if (v) p[k] = v;
    return p;
  }, [filters]);

  const { data: items = [], refetch, isFetching } = useQuery({
    queryKey: ["fu-list", cleanFilters],
    queryFn: async () => (await api.get("/followups", { params: cleanFilters })).data,
  });

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/followups/export/${fmt}`, { params: cleanFilters, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `followups_${new Date().toISOString().slice(0,10)}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const doPrint = () => window.print();

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Follow-up History</h1>
          <p className="text-sm text-[#52525B]">Immutable timeline · every follow-up is permanently recorded.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={doPrint} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-3 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="fu-print"><Printer size={14} /> Print</button>
          <button onClick={() => doExport("pdf")} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-3 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="fu-pdf"><FileText size={14} /> PDF</button>
          <button onClick={() => doExport("xlsx")} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-3 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="fu-xlsx"><FileSpreadsheet size={14} /> Excel</button>
          <button onClick={() => doExport("csv")} className="border border-[#E4E4E7] hover:border-[#0052FF] text-[#06152D] text-sm font-semibold px-3 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="fu-csv"><Download size={14} /> CSV</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <button onClick={() => setFilters({ ...filters, bucket: "today", status: "" })} className={`text-left bg-white border rounded-xl p-4 ${filters.bucket === "today" ? "border-[#0052FF]" : "border-[#E4E4E7]"}`} data-testid="kpi-today">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Today's Follow-ups</div>
          <div className="mt-1 text-3xl font-bold font-mono-num text-[#06152D]">{counts?.today ?? 0}</div>
        </button>
        <button onClick={() => setFilters({ ...filters, bucket: "overdue", status: "" })} className={`text-left bg-white border rounded-xl p-4 ${filters.bucket === "overdue" ? "border-red-500" : "border-[#E4E4E7]"}`} data-testid="kpi-overdue">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-red-600">Overdue</div>
          <div className="mt-1 text-3xl font-bold font-mono-num text-red-700">{counts?.overdue ?? 0}</div>
        </button>
        <button onClick={() => setFilters({ ...filters, bucket: "upcoming", status: "" })} className={`text-left bg-white border rounded-xl p-4 ${filters.bucket === "upcoming" ? "border-[#0052FF]" : "border-[#E4E4E7]"}`} data-testid="kpi-upcoming">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Upcoming (7 days)</div>
          <div className="mt-1 text-3xl font-bold font-mono-num text-[#06152D]">{counts?.upcoming ?? 0}</div>
        </button>
      </div>

      {/* Filters */}
      <div className="mt-4 bg-white border border-[#E4E4E7] rounded-xl p-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#52525B]"><Filter size={14} /> Filters</div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-3">
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v, bucket: "" })}>
            <SelectTrigger data-testid="fu-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="missed">Missed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.followup_type || "all"} onValueChange={(v) => setFilters({ ...filters, followup_type: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="fu-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(CHANNEL_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.executive_id || "all"} onValueChange={(v) => setFilters({ ...filters, executive_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="fu-filter-exec"><SelectValue placeholder="Executive" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All executives</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.customer_id || "all"} onValueChange={(v) => setFilters({ ...filters, customer_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="fu-filter-cust"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value, bucket: "" })} data-testid="fu-filter-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value, bucket: "" })} data-testid="fu-filter-to" />
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-4 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <table className="w-full lj-table" data-testid="fu-history-table">
          <thead>
            <tr>
              <th>Date & Time</th><th>Executive</th><th>Type</th><th>Status</th><th>Outcome</th>
              <th>Customer Response</th><th>Next Follow-up</th><th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="8" className="text-center text-[#52525B] py-8">No follow-ups match your filters.</td></tr>}
            {items.map((f) => (
              <tr key={f.id} data-testid={`fu-row-${f.id}`}>
                <td className="text-xs font-mono-num">{new Date(f.scheduled_at).toLocaleString("en-IN")}</td>
                <td className="text-xs">{f.executive_name || "—"}</td>
                <td className="text-xs">{CHANNEL_LABEL[f.channel] || f.channel}</td>
                <td><Badge className={STATUS_STYLES[f.status]} variant="secondary">{f.status}</Badge></td>
                <td className="text-xs">{f.outcome || "—"}</td>
                <td className="text-xs max-w-[200px]">{f.customer_response || "—"}</td>
                <td className="text-xs font-mono-num">{f.next_followup_date ? new Date(f.next_followup_date).toLocaleString("en-IN") : "—"}</td>
                <td className="text-xs max-w-[250px]">{f.remarks || f.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
